import {
  AppendMessage,
  AssistantRuntimeProvider,
  ThreadMessageLike,
  useExternalStoreRuntime,
} from "@assistant-ui/react";
import { ChatLauncherTab, ChatPanel } from "@truevault/aquilo";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePrimaryOrganization } from "../../hooks/useOrganization";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { RamsayChatMessage, useRamsayChat } from "../../hooks/useRamsayChat";
import { Routes } from "../../root/routes";
import { GLOBAL_ADMIN_EDIT_BAR_ID } from "./GlobalAdminEditBar";

const RAMSAY_CHAT_SIDEBAR_WIDTH_PX = 360;
const RAMSAY_MAX_MESSAGE_LENGTH = 4000;
const RAMSAY_CHAT_OPEN_STORAGE_KEY = "ramsay-chat-sidebar-open";
const BODY_RAMSAY_CHAT_OPEN_CLASS = "has-ramsay-chat-open";
const TOP_BANNER_SLOT_ID = "top-banner";
const PAGE_FOOTER_SELECTOR = ".page-content--footer";
const TOGGLE_BOTTOM_GAP_PX = 16;
// The Help Scout beacon FAB is pinned at bottom: 30px and is ~60px tall (see util/beacon.ts),
// so the closed-state toggle must sit at least this far up to not overlap it.
const TOGGLE_BEACON_CLEARANCE_PX = 106;
const BOTTOM_CHROME_RIGHT_EDGE_SLOP_PX = 48;

const convertMessage = (message: RamsayChatMessage): ThreadMessageLike => ({
  id: message.id,
  role: message.role,
  content: [{ type: "text", text: message.text }],
  metadata: message.isError ? { custom: { isError: true } } : undefined,
});

export const RamsayChatSidebar = () => {
  const [organization] = usePrimaryOrganization();
  const location = useLocation();
  const [storedIsOpen, setStoredIsOpen] = useLocalStorage(RAMSAY_CHAT_OPEN_STORAGE_KEY, "false");
  const isOpen = storedIsOpen === "true";
  const chromeInsets = useViewportChromeInsets();
  // Chat state lives here (not in the panel) so the conversation survives closing the panel.
  const chat = useRamsayChat(organization?.id);

  const runtime = useExternalStoreRuntime({
    messages: chat.messages,
    convertMessage,
    isRunning: chat.isSending,
    onNew: async (message: AppendMessage) => {
      const part = message.content[0];
      if (part?.type !== "text") {
        throw new Error("Only text messages are supported");
      }
      chat.send(part.text);
    },
  });

  const isEnabled = organization?.featureRamsayChat === true;
  const isPreviewPopup = location.pathname === Routes.privacyCenter.previewPopup;
  const shouldRender = isEnabled && !isPreviewPopup;

  useLayoutEffect(() => {
    if (!shouldRender || !isOpen) {
      return;
    }

    document.body.classList.add(BODY_RAMSAY_CHAT_OPEN_CLASS);
    return () => document.body.classList.remove(BODY_RAMSAY_CHAT_OPEN_CLASS);
  }, [isOpen, shouldRender]);

  // Warm the conversation's backing VM while the user types their first message, so it is less
  // likely to hit the ~70s cold start. Re-fires on navigation so an open-but-idle panel keeps
  // its VM alive past the server's 15-minute idle timeout; throttled inside the hook.
  const warm = chat.warm;
  useEffect(() => {
    if (shouldRender && isOpen) {
      warm();
    }
  }, [isOpen, shouldRender, warm, location.pathname]);

  if (!shouldRender) {
    return null;
  }

  const setOpen = (open: boolean) => setStoredIsOpen(open ? "true" : "false");

  return (
    <>
      {!isOpen && (
        <div
          className="tw-boundary"
          style={{
            position: "fixed",
            right: 0,
            bottom: Math.max(
              chromeInsets.bottom + TOGGLE_BOTTOM_GAP_PX,
              TOGGLE_BEACON_CLEARANCE_PX,
            ),
            zIndex: 20,
          }}
        >
          <ChatLauncherTab label="Ramsay" onClick={() => setOpen(true)} />
        </div>
      )}
      {isOpen && (
        <aside
          className="tw-boundary"
          aria-label="Ramsay chat"
          style={{
            position: "fixed",
            top: chromeInsets.top,
            right: 0,
            bottom: chromeInsets.bottom,
            width: RAMSAY_CHAT_SIDEBAR_WIDTH_PX,
            zIndex: 20,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            background: "#ffffff",
            borderLeft: "1px solid #E8E5EE",
            boxShadow: "0 0 24px rgba(15, 23, 42, 0.08)",
          }}
        >
          <AssistantRuntimeProvider runtime={runtime}>
            <ChatPanel
              title="Ramsay"
              onClose={() => setOpen(false)}
              emptyStateTitle="Ask Ramsay"
              emptyStateSubtitle="How can I help with your privacy program?"
              inputPlaceholder="Ask a question…"
              maxMessageLength={RAMSAY_MAX_MESSAGE_LENGTH}
              slowResponseNotice="Ramsay is getting ready, be with you shortly…"
            />
          </AssistantRuntimeProvider>
        </aside>
      )}
    </>
  );
};

const useViewportChromeInsets = () => {
  const location = useLocation();
  const [insets, setInsets] = useState({ top: 0, bottom: 0 });

  useLayoutEffect(() => {
    let frame = 0;
    const observed = new Set<Element>();
    const resizeObserver = new ResizeObserver(() => scheduleMeasure());

    const observe = (element: Element | null) => {
      if (!element || observed.has(element)) {
        return;
      }
      observed.add(element);
      resizeObserver.observe(element);
    };

    const syncObservedElements = () => {
      observe(document.getElementById(TOP_BANNER_SLOT_ID));
      document.querySelectorAll(`#${TOP_BANNER_SLOT_ID} > *`).forEach(observe);
      observe(document.getElementById("page-footer"));
      observe(document.querySelector(PAGE_FOOTER_SELECTOR));
      observe(document.getElementById(GLOBAL_ADMIN_EDIT_BAR_ID));
      scheduleMeasure();
    };

    const measure = () => {
      setInsets({
        top: measureTopChromeHeight(),
        bottom: measureBottomChromeHeight(),
      });
    };

    const scheduleMeasure = () => {
      if (frame) {
        return;
      }
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };

    const mutationObserver = new MutationObserver(syncObservedElements);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", scheduleMeasure);
    syncObservedElements();

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [location.pathname]);

  return insets;
};

const measureTopChromeHeight = () => {
  const slot = document.getElementById(TOP_BANNER_SLOT_ID);
  if (!slot) {
    return 0;
  }

  const childHeights = Array.from(slot.children, (child) => visibleHeight(child));
  return Math.ceil(Math.max(0, ...childHeights, 0));
};

const measureBottomChromeHeight = () => {
  return Math.max(
    occupyingViewportBottomHeight(document.querySelector(PAGE_FOOTER_SELECTOR)),
    occupyingViewportBottomHeight(document.getElementById(GLOBAL_ADMIN_EDIT_BAR_ID)),
  );
};

const occupyingViewportBottomHeight = (element: Element | null) => {
  if (!element) {
    return 0;
  }

  const rect = element.getBoundingClientRect();
  if (rect.height <= 0) {
    return 0;
  }

  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const sitsOnViewportBottom = rect.bottom >= viewportHeight - 2;
  const occupiesRightEdge = rect.right >= viewportWidth - BOTTOM_CHROME_RIGHT_EDGE_SLOP_PX;

  if (!sitsOnViewportBottom || !occupiesRightEdge) {
    return 0;
  }

  return Math.ceil(Math.max(0, viewportHeight - rect.top));
};

const visibleHeight = (element: Element) => {
  if (!(element instanceof HTMLElement)) {
    return 0;
  }

  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") {
    return 0;
  }

  return element.getBoundingClientRect().height;
};
